import { useEffect, useState } from 'react';
import { IconArrowDown, IconDots } from '@belong/icons';
import { Text, Image, Modal, ButtonBase } from '@belong/ui';
import { MaintenanceModel } from 'api/models';
import { clsx } from 'clsx';
import { noop, kebabCase, startCase } from 'es-toolkit';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { STRINGS } from 'pages/PostInspectionFlow/strings';
import css from './belong-standard-improvement-link.module.css';

type Props = {
  belongStandardCategory: string;
  assessment: MaintenanceModel['assessment'];
};

export function BelongStandardImprovementLink({
  belongStandardCategory,
  assessment,
}: Props) {
  const [isBelongStandardsModalOpen, setIsBelongStandardsModalOpen] =
    useState(false);
  const { isEnabled: isBelongStandardsEnabled } =
    useFeatureFlag('belong-standards');

  const belongStandardsUrl = `${
    window.location.origin
  }/belong-standards?activeModal=BELONG_STANDARDS&standard=${kebabCase(belongStandardCategory)}`;

  const formattedBelongStandardCategory = startCase(
    belongStandardCategory,
  ).replace('And', '&');

  function handleBelongStandardsModalOpen() {
    setIsBelongStandardsModalOpen(true);
  }

  function handleBelongStandardsModalDismiss() {
    setIsBelongStandardsModalOpen(false);
  }

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (
        event.data?.type === 'CLOSE_BELONG_STANDARDS_MODAL' &&
        event.data?.source === 'belong-standards-iframe'
      ) {
        handleBelongStandardsModalDismiss();
      }
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  if (!isBelongStandardsEnabled) {
    return null;
  }

  const getTitle = () => {
    if (assessment) {
      if (assessment === 'Required') {
        return STRINGS.requiredApplication;
      }
      if (assessment === 'Recommended') {
        return STRINGS.recommendedApplication;
      }
    }
    return STRINGS.standardApplication;
  };

  return (
    <>
      <ButtonBase
        className="relative flex w-full flex-col pt-xl"
        onClick={handleBelongStandardsModalOpen}
      >
        <div
          className={clsx(
            'group relative flex flex-col rounded',
            css.gradientBorder,
          )}
        >
          <div className="absolute -top-xl left-1/2 h-xl w-[2px] transition-all duration-hover ease-in-out group-hover:opacity-0">
            <Image src="modals/belong-standards/standard_line.svg" alt="" />
          </div>
          <div className="absolute -top-xl left-1/2 h-xl w-[2px] opacity-0 transition-all duration-hover ease-in-out group-hover:opacity-100">
            <Image
              src="modals/belong-standards/standard_line_hover_dark_green.svg"
              alt=""
            />
          </div>
          <div className="absolute inset-0 h-full w-full rounded border-2 border-solid border-transparent transition-all duration-hover group-hover:border-dark-green" />
          <div className="relative overflow-hidden p-sm pb-[6px] lg:pb-sm">
            <div className="flex flex-col items-center justify-start gap-xs lg:flex-row lg:justify-between">
              <div className="contents lg:flex lg:max-w-[90%] lg:items-center lg:gap-xs">
                <div className="h-xl w-[50px] shrink-0">
                  <Image
                    src="modals/belong-standards/belong_standard_badge_sm.png"
                    className="object-cover"
                    alt="Belong Standard Badge"
                  />
                </div>
                <div className="relative w-full max-w-3xl text-center lg:min-h-[40px] lg:max-w-none lg:text-left">
                  <Text as="span">
                    <Text as="span" fontWeight="semibold">
                      {getTitle()}
                    </Text>{' '}
                    <Text
                      as="span"
                      fontWeight="semibold"
                      className="text-green"
                    >
                      {formattedBelongStandardCategory}&nbsp;{STRINGS.standard}.
                    </Text>
                  </Text>
                </div>
              </div>
              <div className="z-10 mt-0 flex items-center justify-center lg:mt-0 lg:justify-end">
                <IconArrowDown className="hidden h-sm w-sm -rotate-90 lg:block [&>path]:fill-navy" />
                <div className="mr-2xs flex items-center justify-center lg:hidden">
                  <IconDots className="h-lg w-sm rotate-90 [&>path]:fill-navy" />
                </div>
              </div>
            </div>
            <div className="absolute -bottom-xs -right-xs z-0 h-5xl w-5xl lg:left-auto lg:right-0 lg:h-3xl lg:w-3xl lg:-rotate-12">
              <Image
                src="modals/belong-standards/belong_standard_badge_gray.svg"
                className="object-cover"
                alt="Belong Standard Badge"
              />
            </div>
          </div>
        </div>
      </ButtonBase>
      <Modal
        isOpen={isBelongStandardsModalOpen}
        onDismiss={handleBelongStandardsModalDismiss}
        name="belong-standards-modal-iframe"
        size="fullScreen"
        childrenClassName="!py-0 md:!py-0"
        overlayClassName="!z-[999999999] md:!-mt-2xl bg-dark-navy md:bg-white-translucent overflow-y-hidden"
        closeButton={() => noop}
      >
        <div className="mx-auto flex h-full w-full flex-col">
          <iframe
            src={belongStandardsUrl}
            className="h-screen w-full border-0"
            title="Belong Standards"
            allowFullScreen
          />
        </div>
      </Modal>
    </>
  );
}
